import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground hover:bg-primary/90',
                destructive:
                    'border border-destructive/40 bg-transparent text-destructive hover:border-destructive/70 hover:bg-destructive/10 focus-visible:ring-destructive/80 dark:focus-visible:ring-destructive/80',
                success:
                    'bg-success text-success-foreground hover:bg-success/90 focus-visible:ring-success/20 dark:focus-visible:ring-success/40',
                outline:
                    'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
                link: 'text-primary underline-offset-4 hover:underline',
                cyber: 'border bg-transparent hover:bg-accent relative overflow-hidden',
                'cyber-long':
                    // Emissive slab: top-lit gradient + outer bloom + inner top
                    // highlight — the button reads as a light source, not paint.
                    // `st-btn-long` is the Speed skin's hook (speed-ui.css).
                    'st-btn-long bg-gradient-to-b from-[#d9fa53] to-[var(--primary)] text-primary-foreground font-semibold shadow-[0_0_28px_-6px_rgba(200,242,36,0.45),inset_0_1px_0_rgba(255,255,255,0.35)] transition-shadow hover:shadow-[0_0_36px_-4px_rgba(200,242,36,0.65),inset_0_1px_0_rgba(255,255,255,0.4)]',
                'cyber-short':
                    // `st-btn-short` is the Speed skin's hook (speed-ui.css).
                    'st-btn-short border border-destructive/45 bg-transparent text-destructive font-semibold hover:border-destructive/75 hover:bg-destructive/10',
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
                lg: 'h-10 px-6 has-[>svg]:px-4',
                icon: 'size-9',
                'icon-sm': 'size-8',
                'icon-lg': 'size-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

function Button({
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    ...props
}: React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
    }) {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
}

export { Button, buttonVariants };
